import { z } from 'zod';
import { CH_ID_MAX_LENGTH } from './change-healthcare';

// Upper bound on the free-text prescription columns (drug name, sig, pharmacy
// and prescriber names), shared by the DB length CHECKs and the Change
// Healthcare history codec, which rejects a longer value, so a value the codec
// accepts the CHECK never refuses. The identifier columns use CH_ID_MAX_LENGTH
// the same way.
export const RX_TEXT_MAX_LENGTH = 4096;

// The largest value the integer count columns (days supply, quantity, refills)
// hold: Postgres int4. The codec rejects a larger count rather than letting the
// write overflow.
export const RX_COUNT_MAX = 2_147_483_647;

// Why a copy run escalated to the Rx support channel. Closed so the Slack card
// and the metric tag stay bounded.
export const RX_SYNC_ISSUE_KINDS = [
  'sweep_failed',
  'reconcile_unrepaired',
  'unattributable',
] as const;
export type RxSyncIssueKind = (typeof RX_SYNC_ISSUE_KINDS)[number];

// The most facility ids one escalation names, so a run failing across every
// facility still renders a bounded card.
export const RX_SYNC_ISSUE_MAX_FACILITY_IDS = 10;

// The shape an rx_status may take once it leaves in an event: a short
// uppercase code, judged after trim and uppercase. CH's value is free text, so
// anything else is reported as RX_STATUS_OTHER rather than forwarded; the row
// keeps the raw value.
export const RX_STATUS_CODE = /^[A-Z][A-Z0-9_ ]{0,31}$/;
export const RX_STATUS_OTHER = 'OTHER';

// Pointer-shaped payload for the rx.prescription.* outbox events (ADR-052). The
// member travels in the envelope actor; no drug name, sig, NPI, pharmacy or rx
// number is ever carried, and `.strict()` turns any extra field into a parse
// failure.
export const rxPrescriptionChangePayloadSchema = z
  .object({
    prescriptionId: z.string().uuid(),
    changeHealthcareIdRef: z.string().uuid(),
    facilityId: z.string().max(CH_ID_MAX_LENGTH),
    rxStatus: z.string().regex(RX_STATUS_CODE).nullable(),
  })
  .strict();
export type RxPrescriptionChangePayload = z.infer<typeof rxPrescriptionChangePayloadSchema>;

// Aggregated per run, so it names no member: support finds the patients from
// the sync logs, which carry change_healthcare_ids row ids.
export const rxPrescriptionSyncIssuePayloadSchema = z
  .object({
    issueKind: z.enum(RX_SYNC_ISSUE_KINDS),
    failureClass: z.string().max(64).nullable(),
    count: z.number().int().nonnegative(),
    facilityIds: z.array(z.string().max(CH_ID_MAX_LENGTH)).max(RX_SYNC_ISSUE_MAX_FACILITY_IDS),
  })
  .strict();
export type RxPrescriptionSyncIssuePayload = z.infer<typeof rxPrescriptionSyncIssuePayloadSchema>;
